import React, { useContext, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsAvailableForRepetitionSelect.module.scss';

interface IRestaurantsAvailableForRepetitionSelect {
	setIDsOfRestaurantsAvailableForRepetition: (value: string[]) => void;
	IDSelectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	IDsOfRestaurantsAvailableForRepetition: string[];
	IDsSelectedRestaurant: string[];
}

const RestaurantsAvailableForRepetitionSelect: React.FC<IRestaurantsAvailableForRepetitionSelect> = ({
	setIDsOfRestaurantsAvailableForRepetition,
	IDSelectedCustomer,
	fetchedRestaurants,
	IDsOfRestaurantsAvailableForRepetition,
	IDsSelectedRestaurant,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const restaurants = useMemo(() => {
		return hasAccess ? fetchedRestaurants : loggedUser.restaurants;
	}, [hasAccess, fetchedRestaurants, loggedUser]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsOfRestaurantsAvailableForRepetition.filter((el) => el !== id);
			setIDsOfRestaurantsAvailableForRepetition(updatedSelectedItems);
		},
		[IDsOfRestaurantsAvailableForRepetition, setIDsOfRestaurantsAvailableForRepetition],
	);

	const restaurantsList = useMemo(() => {
		return IDsOfRestaurantsAvailableForRepetition.map((el) => {
			const label = restaurants?.find((item) => item.id === el)?.name || '';

			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [IDsOfRestaurantsAvailableForRepetition, restaurants, handleRemove]);

	return (
		<div className={style.container} data-test="RestaurantsAvailableForRepetitionSelect">
			<div className={style.field}>
				<MultipleSelect
					data={restaurants || []}
					setIDsOfRestaurantsAvailableForRepetition={setIDsOfRestaurantsAvailableForRepetition}
					IDSelectedCustomer={IDSelectedCustomer}
					IDsOfRestaurantsAvailableForRepetition={IDsOfRestaurantsAvailableForRepetition}
					IDsSelectedRestaurant={IDsSelectedRestaurant}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsAvailableForRepetitionSelect);
